(function () {
  'use strict';

  const TAG_NAME = 'cart-discount';
  const SELECTORS = {
    accordion: '[data-cart-discount-accordion]',
    toggle: '[data-cart-discount-toggle]',
    form: '[data-cart-discount-form]',
    input: '[data-cart-discount-input]',
    submit: '[data-cart-discount-submit]',
    error: '[data-cart-discount-error]',
    applied: '[data-cart-discount-applied]',
  };
  const EVENTS = {
    cartRefresh: 'theme:cart:refresh',
    cartChange: 'theme:cart:change',
  };

  function cartUrl() {
    return `${window.Shopify?.routes?.root || '/'}cart`;
  }

  async function fetchCart() {
    const response = await fetch(`${cartUrl()}.js`, {
      headers: { Accept: 'application/json' },
    });

    if (!response.ok) {
      throw new Error('Unable to load cart');
    }

    return response.json();
  }

  function getDiscountCodes(cart) {
    if (!cart || !Array.isArray(cart.discount_codes)) {
      return [];
    }

    return cart.discount_codes.filter((code) => code && code.code);
  }

  function renderAppliedCodes(container, cart) {
    if (!container) {
      return;
    }

    const codes = getDiscountCodes(cart);
    const hasApplicableCode = codes.some((code) => code.applicable !== false);

    container.hidden = !hasApplicableCode;
    container.innerHTML = '';

    codes.forEach((code) => {
      if (code.applicable === false) {
        return;
      }

      const badge = document.createElement('span');
      badge.className = 'cart-discount__applied-code';
      badge.textContent = code.code;
      container.appendChild(badge);
    });
  }

  if (customElements.get(TAG_NAME)) {
    return;
  }

  customElements.define(
    TAG_NAME,
    class CartDiscount extends HTMLElement {
      connectedCallback() {
        this.accordion = this.querySelector(SELECTORS.accordion);
        this.toggle = this.querySelector(SELECTORS.toggle);
        this.form = this.querySelector(SELECTORS.form);
        this.input = this.querySelector(SELECTORS.input);
        this.submitButton = this.querySelector(SELECTORS.submit);
        this.errorMessage = this.querySelector(SELECTORS.error);
        this.appliedWrap = this.querySelector(SELECTORS.applied);
        this.defaultError = this.dataset.errorMessage || 'Invalid discount code';

        this.handleToggle = this.handleToggle.bind(this);
        this.handleSubmit = this.handleSubmit.bind(this);
        this.handleCartChange = this.handleCartChange.bind(this);

        this.toggle?.addEventListener('click', this.handleToggle);
        this.form?.addEventListener('submit', this.handleSubmit);
        document.addEventListener(EVENTS.cartChange, this.handleCartChange);

        this.syncAppliedCodes();
      }

      disconnectedCallback() {
        this.toggle?.removeEventListener('click', this.handleToggle);
        this.form?.removeEventListener('submit', this.handleSubmit);
        document.removeEventListener(EVENTS.cartChange, this.handleCartChange);
      }

      handleToggle() {
        if (!this.accordion || !this.toggle) {
          return;
        }

        const isOpen = this.accordion.classList.toggle('is-open');
        this.toggle.setAttribute('aria-expanded', isOpen ? 'true' : 'false');
      }

      async syncAppliedCodes() {
        try {
          const cart = await fetchCart();
          renderAppliedCodes(this.appliedWrap, cart);
        } catch (error) {
          // Ignore cart fetch errors during background sync.
        }
      }

      showError(message) {
        if (!this.errorMessage) {
          return;
        }

        this.errorMessage.textContent = message;
        this.errorMessage.hidden = false;
      }

      clearError() {
        if (!this.errorMessage) {
          return;
        }

        this.errorMessage.textContent = '';
        this.errorMessage.hidden = true;
      }

      setLoading(isLoading) {
        this.classList.toggle('is-loading', isLoading);

        if (this.submitButton) {
          this.submitButton.disabled = isLoading;
        }

        if (this.input) {
          this.input.disabled = isLoading;
        }
      }

      async handleSubmit(event) {
        event.preventDefault();

        const code = (this.input?.value || '').trim();

        this.clearError();

        if (!code) {
          this.showError(this.defaultError);
          return;
        }

        this.setLoading(true);

        try {
          const response = await fetch(`${cartUrl()}/update.js`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Accept: 'application/json',
            },
            body: JSON.stringify({ discount: code }),
          });

          const cart = await response.json();

          if (!response.ok || cart.status) {
            throw new Error(cart.description || cart.message || this.defaultError);
          }

          const codes = getDiscountCodes(cart);
          const matchedCode = codes.find(
            (discountCode) => discountCode.code.toLowerCase() === code.toLowerCase()
          );

          if (matchedCode && matchedCode.applicable === false) {
            throw new Error(this.defaultError);
          }

          if (
            !matchedCode &&
            !codes.some((discountCode) => discountCode.applicable !== false) &&
            (cart.cart_level_discount_applications?.length || 0) === 0
          ) {
            throw new Error(this.defaultError);
          }

          if (this.input) {
            this.input.value = '';
          }

          renderAppliedCodes(this.appliedWrap, cart);
          document.dispatchEvent(new CustomEvent(EVENTS.cartRefresh, { bubbles: true }));
        } catch (error) {
          this.showError(error.message || this.defaultError);
        } finally {
          this.setLoading(false);
        }
      }

      handleCartChange() {
        this.syncAppliedCodes();
      }
    }
  );
})();
